<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Create Order
        </v-card-title>
        <v-card-text>
          <v-form ref="orderForm">
            <v-row>
              <!-- Customer Selection -->
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.customer_id"
                  :items="customerItems"
                  outlined
                  label="Customer *"
                  prepend-inner-icon="mdi-account"
                  :rules="[rules.required]"
                  clearable
                ></v-autocomplete>
              </v-col>

              <!-- Store Selection -->
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.store_id"
                  :items="storeItems"
                  outlined
                  label="Store *"
                  prepend-inner-icon="mdi-store"
                  :rules="[rules.required]"
                  clearable
                ></v-autocomplete>
              </v-col>

              <!-- Address -->
              <v-col sm="12">
                <v-textarea
                  v-model="form.address"
                  outlined
                  label="Delivery Address *"
                  prepend-inner-icon="mdi-map-marker"
                  :rules="[rules.required]"
                  rows="3"
                ></v-textarea>
              </v-col>

              <!-- Payment Mode -->
              <v-col sm="6">
                <v-select
                  v-model="form.payment_mode"
                  :items="paymentModes"
                  outlined
                  label="Payment Mode *"
                  prepend-inner-icon="mdi-cash"
                  :rules="[rules.required]"
                ></v-select>
              </v-col>

              <!-- Total Amount -->
              <v-col sm="6">
                <v-text-field
                  v-model.number="form.total_amount"
                  outlined
                  label="Total Amount *"
                  type="number"
                  prepend-inner-icon="mdi-currency-inr"
                  :rules="[rules.required]"
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <!-- Order Details Section -->
            <v-row>
              <v-col sm="12">
                <h3 class="mb-4">Order Items</h3>
              </v-col>

              <v-col
                sm="12"
                v-for="(item, index) in form.order_details"
                :key="index"
              >
                <v-card outlined class="pa-4 mb-3">
                  <v-row>
                    <!-- Product Selection -->
                    <v-col sm="4">
                      <v-autocomplete
                        v-model="item.product_id"
                        :items="productItems"
                        outlined
                        label="Product *"
                        prepend-inner-icon="mdi-package-variant"
                        :rules="[rules.required]"
                        @change="getProductVariants(index)"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <!-- Product Variant Selection -->
                    <v-col sm="4">
                      <v-autocomplete
                        v-model="item.product_variant_id"
                        :items="item.variantItems || []"
                        outlined
                        label="Variant *"
                        prepend-inner-icon="mdi-size-s"
                        :rules="[rules.required]"
                        @change="updateItemPrice(index)"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <!-- Quantity -->
                    <v-col sm="2">
                      <v-text-field
                        v-model.number="item.quantity"
                        outlined
                        label="Quantity *"
                        type="number"
                        min="1"
                        prepend-inner-icon="mdi-counter"
                        :rules="[rules.required]"
                        @input="calculateItemTotal(index)"
                      ></v-text-field>
                    </v-col>

                    <!-- Price -->
                    <v-col sm="2">
                      <v-text-field
                        v-model.number="item.price"
                        outlined
                        label="Price *"
                        type="number"
                        prepend-inner-icon="mdi-currency-inr"
                        :rules="[rules.required]"
                        @input="calculateItemTotal(index)"
                      ></v-text-field>
                    </v-col>

                    <!-- Total Price -->
                    <v-col sm="6">
                      <v-text-field
                        v-model.number="item.total_price"
                        outlined
                        label="Total Price *"
                        type="number"
                        prepend-inner-icon="mdi-calculator"
                        readonly
                      ></v-text-field>
                    </v-col>

                    <!-- Remove Item Button -->
                    <v-col sm="6" class="text-right">
                      <v-btn
                        v-if="form.order_details.length > 1"
                        color="error"
                        @click="removeOrderDetail(index)"
                      >
                        <v-icon left>mdi-delete</v-icon>
                        Remove Item
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>

              <!-- Add More Items Button -->
              <v-col sm="12">
                <v-btn color="primary" outlined @click="addOrderDetail">
                  <v-icon left>mdi-plus</v-icon>
                  Add Another Item
                </v-btn>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <!-- Action Buttons -->
            <v-btn
              rounded
              color="primary"
              :loading="isLoading"
              :disabled="isLoading"
              @click="save"
            >
              Create Order
            </v-btn>
            <v-btn rounded class="ml-2" @click="$router.back()">
              Cancel
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "OrderCreatePage",
  data() {
    return {
      form: {
        customer_id: "",
        store_id: "",
        address: "",
        payment_mode: "",
        total_amount: 0,
        order_details: [
          {
            product_id: "",
            product_variant_id: "",
            quantity: 1,
            price: 0,
            total_price: 0,
            variantItems: [],
          },
        ],
      },
      customerItems: [],
      storeItems: [],
      productItems: [],
      paymentModes: [
        { text: "Cash on Delivery", value: 0 },
        { text: "UPI", value: 1 },
        { text: "Card", value: 2 },
        { text: "Bank Transfer", value: 3 },
      ],
      rules: {
        required: (v) => !!v || "This field is required",
      },
      isLoading: false,
    };
  },
  mounted() {
    this.getMasters();
  },
  methods: {
    async getMasters() {
      try {
        // Fetch customers
        const customersRes = await axios.get("/users");
        if (customersRes.data.success) {
          this.customerItems = customersRes.data.data.map((user) => ({
            text: `${user.first_name} ${user.last_name || ""} (${user.mobile})`,
            value: user.id,
          }));
        }

        // Fetch stores
        const storesRes = await axios.get("/stores");
        if (storesRes.data.success) {
          this.storeItems = storesRes.data.data.map((store) => ({
            text: store.name,
            value: store.id,
          }));
        }

        // Fetch products
        const productsRes = await axios.get("/products");
        if (productsRes.data.success) {
          this.productItems = productsRes.data.data.map((product) => ({
            text: product.name,
            value: product.id,
          }));
        }
      } catch (error) {
        console.error("Error loading masters:", error);
        this.$toast.error("Failed to load form data", {
          position: "top-right",
          timeout: 3000,
        });
      }
    },
    async getProductVariants(index) {
      const item = this.form.order_details[index];
      if (!item.product_id) {
        item.variantItems = [];
        return;
      }

      try {
        const response = await axios.get(
          `/products_variants?product_id=${item.product_id}`
        );
        if (response.data.success) {
          item.variantItems = response.data.data.map((variant) => ({
            text: `${variant.size || ""} ${variant.color || ""} - ₹${variant.sale_price}`,
            value: variant.id,
            price: variant.sale_price,
          }));
        }
      } catch (error) {
        console.error("Error loading variants:", error);
      }
    },
    updateItemPrice(index) {
      const item = this.form.order_details[index];
      const selectedVariant = item.variantItems?.find(
        (v) => v.value === item.product_variant_id
      );
      if (selectedVariant) {
        item.price = selectedVariant.price;
        this.calculateItemTotal(index);
      }
    },
    calculateItemTotal(index) {
      const item = this.form.order_details[index];
      item.total_price = item.quantity * item.price;
      this.calculateTotalAmount();
    },
    calculateTotalAmount() {
      this.form.total_amount = this.form.order_details.reduce(
        (sum, item) => sum + (item.total_price || 0),
        0
      );
    },
    addOrderDetail() {
      this.form.order_details.push({
        product_id: "",
        product_variant_id: "",
        quantity: 1,
        price: 0,
        total_price: 0,
        variantItems: [],
      });
    },
    removeOrderDetail(index) {
      this.form.order_details.splice(index, 1);
      this.calculateTotalAmount();
    },
    async save() {
      if (!this.$refs.orderForm.validate()) {
        this.$toast.error("Please fill all required fields", {
          position: "top-right",
          timeout: 3000,
        });
        return;
      }

      try {
        this.isLoading = true;
        await axios.post("/orders", this.form);
        this.$toast.success("Order created successfully!", {
          position: "top-right",
          timeout: 3000,
        });
        this.$router.push("/orders");
      } catch (error) {
        console.error("Error creating order:", error);
        this.$toast.error(
          error.response?.data?.message || "Failed to create order",
          {
            position: "top-right",
            timeout: 3000,
          }
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.v-card {
  transition: all 0.3s;
}
</style>
